import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { fetchSocialAccounts, createSocialPost } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Send, Plus, X, CheckCircle2, Loader2, AlertTriangle, MessageCircle } from "lucide-react";
import { PLATFORM_MAP } from "@/lib/constants";
import { Platform, type SocialPost } from "@/types";
import { cn } from "@/lib/utils";
import { useState, useCallback } from "react";
import { PageShell } from "@/components/page-shell";

export const Route = createFileRoute("/posts/")({
  component: PostsPage,
});

function platformIcon(id: string) {
  return PLATFORM_MAP[id as Platform]?.icon ?? MessageCircle;
}

function platformColor(id: string) {
  return PLATFORM_MAP[id as Platform]?.color ?? "text-slate-500";
}

function platformLabel(id: string) {
  return PLATFORM_MAP[id as Platform]?.label ?? id;
}

function PostsPage() {
  const [caption, setCaption] = useState("");
  const [selectedAccounts, setSelectedAccounts] = useState<string[]>([]);
  const [mediaUrls, setMediaUrls] = useState<string[]>([]);
  const [scheduledAt, setScheduledAt] = useState("");
  const [isDraft, setIsDraft] = useState(false);
  const [createdPost, setCreatedPost] = useState<SocialPost | null>(null);

  const queryClient = useQueryClient();

  const { data: accounts, isLoading: accountsLoading } = useQuery({
    queryKey: ["social-accounts"],
    queryFn: () => fetchSocialAccounts(),
  });

  const createMutation = useMutation({
    mutationFn: createSocialPost,
    onSuccess: (data) => {
      setCreatedPost(data);
      setCaption("");
      setSelectedAccounts([]);
      setMediaUrls([]);
      setScheduledAt("");
      queryClient.invalidateQueries({ queryKey: ["social-posts"] });
    },
  });

  const toggleAccount = useCallback((id: string) => {
    setSelectedAccounts((prev) =>
      prev.includes(id) ? prev.filter((a) => a !== id) : [...prev, id],
    );
  }, []);

  const handleSubmit = () => {
    if (!caption.trim()) return;
    if (selectedAccounts.length === 0) return;

    createMutation.mutate({
      caption: caption.trim(),
      social_accounts: selectedAccounts,
      scheduled_at: scheduledAt ? new Date(scheduledAt).toISOString() : null,
      media: mediaUrls.filter((u) => u.trim()).map((url) => ({ url })),
      isDraft,
    });
  };

  const connectedAccounts = accounts?.filter((a) => a.status === "connected") ?? [];

  // Group by platform for display
  const byPlatform: Record<string, typeof connectedAccounts> = {};
  for (const acc of connectedAccounts) {
    const arr = byPlatform[acc.platform] ?? [];
    arr.push(acc);
    byPlatform[acc.platform] = arr;
  }

  return (
    <PageShell title="New Post" backTo="/" maxWidth="2xl">
      <div className="space-y-6">
        {/* Success state */}
        {createdPost && (
          <div className="rounded-xl border bg-emerald-50 p-4 flex items-start gap-3">
            <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="text-sm font-medium text-emerald-800">Post created!</div>
              <div className="text-xs text-emerald-600 mt-0.5 font-mono">
                {String(createdPost["id"] ?? "N/A")}
              </div>
              <div className="text-xs text-emerald-600 mt-1">
                {isDraft ? "Saved as draft" : scheduledAt ? "Scheduled" : "Posting now"}
              </div>
            </div>
            <button
              onClick={() => setCreatedPost(null)}
              className="text-emerald-600 hover:text-emerald-800"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Caption */}
        <div>
          <label className="text-sm font-medium mb-1.5 block">Caption</label>
          <textarea
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            placeholder="What's on your mind?"
            rows={4}
            className="w-full px-3 py-2.5 text-sm rounded-xl border bg-background resize-none focus:ring-2 focus:ring-primary/20 focus:outline-none placeholder:text-muted-foreground/60"
          />
          <div className="text-xs text-muted-foreground mt-1 text-right">{caption.length}</div>
        </div>

        {/* Media URLs */}
        <div>
          <label className="text-sm font-medium mb-1.5 block">Media URLs</label>
          <div className="space-y-2">
            {mediaUrls.map((url, i) => (
              <div key={i} className="flex items-center gap-2">
                <input
                  type="text"
                  value={url}
                  onChange={(e) => {
                    const next = [...mediaUrls];
                    next[i] = e.target.value;
                    setMediaUrls(next);
                  }}
                  placeholder="https://..."
                  className="flex-1 px-3 py-2 text-sm rounded-lg border bg-background focus:ring-2 focus:ring-primary/20 focus:outline-none placeholder:text-muted-foreground/60"
                />
                <Button
                  variant="ghost"
                  size="icon"
                  className="w-8 h-8 shrink-0"
                  onClick={() => setMediaUrls((prev) => prev.filter((_, idx) => idx !== i))}
                >
                  <X className="w-3.5 h-3.5" />
                </Button>
              </div>
            ))}
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5"
              onClick={() => setMediaUrls((prev) => [...prev, ""])}
            >
              <Plus className="w-3.5 h-3.5" />
              Add media URL
            </Button>
          </div>
        </div>

        {/* Account selector */}
        <div>
          <label className="text-sm font-medium mb-1.5 block">
            Accounts{" "}
            {selectedAccounts.length > 0 && (
              <span className="text-muted-foreground font-normal">({selectedAccounts.length})</span>
            )}
          </label>
          {accountsLoading ? (
            <div className="text-sm text-muted-foreground py-4">Loading accounts...</div>
          ) : connectedAccounts.length === 0 ? (
            <div className="rounded-lg border border-dashed p-4 text-center text-sm text-muted-foreground">
              No connected accounts.{" "}
              <Link to="/accounts" className="text-primary underline">
                Connect one
              </Link>
            </div>
          ) : (
            <div className="space-y-3">
              {Object.entries(byPlatform).map(([platform, platformAccounts]) => {
                const Icon = platformIcon(platform);
                const allSelected = platformAccounts.every((a) => selectedAccounts.includes(a.id));
                const someSelected = platformAccounts.some((a) => selectedAccounts.includes(a.id));
                return (
                  <div key={platform} className="rounded-xl border bg-card overflow-hidden">
                    <button
                      onClick={() => {
                        if (allSelected) {
                          setSelectedAccounts((prev) =>
                            prev.filter((id) => !platformAccounts.find((a) => a.id === id)),
                          );
                        } else {
                          setSelectedAccounts((prev) => [
                            ...new Set([...prev, ...platformAccounts.map((a) => a.id)]),
                          ]);
                        }
                      }}
                      className={cn(
                        "flex items-center gap-2 w-full px-4 py-2.5 border-b text-left hover:bg-muted/40 transition-colors",
                        platformColor(platform),
                      )}
                    >
                      <div
                        className={cn(
                          "w-4 h-4 rounded border flex items-center justify-center transition-colors",
                          allSelected
                            ? "bg-primary border-primary"
                            : someSelected
                              ? "bg-primary/50 border-primary"
                              : "border-muted-foreground/40",
                        )}
                      >
                        {(allSelected || someSelected) && (
                          <CheckCircle2 className="w-3 h-3 text-white" />
                        )}
                      </div>
                      <Icon className="w-4 h-4" />
                      <span className="text-sm font-medium capitalize">
                        {platformLabel(platform)}
                      </span>
                      <span className="text-xs text-muted-foreground ml-auto">
                        {platformAccounts.length}
                      </span>
                    </button>
                    <div className="divide-y">
                      {platformAccounts.map((acc) => (
                        <button
                          key={acc.id}
                          onClick={() => toggleAccount(acc.id)}
                          className="flex items-center gap-3 w-full px-4 py-2.5 text-left hover:bg-muted/40 transition-colors"
                        >
                          <div
                            className={cn(
                              "w-4 h-4 rounded border flex items-center justify-center transition-colors",
                              selectedAccounts.includes(acc.id)
                                ? "bg-primary border-primary"
                                : "border-muted-foreground/40",
                            )}
                          >
                            {selectedAccounts.includes(acc.id) && (
                              <CheckCircle2 className="w-3 h-3 text-white" />
                            )}
                          </div>
                          {acc.profilePhotoUrl ? (
                            <img
                              src={acc.profilePhotoUrl}
                              alt=""
                              referrerPolicy="no-referrer"
                              className="w-8 h-8 rounded-full object-cover bg-muted"
                            />
                          ) : (
                            <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center">
                              <Icon className={cn("w-4 h-4", platformColor(platform))} />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="text-sm truncate">{acc.username ?? acc.userId}</div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Schedule */}
        <div>
          <label className="text-sm font-medium mb-1.5 block">Schedule</label>
          <input
            type="datetime-local"
            value={scheduledAt}
            onChange={(e) => setScheduledAt(e.target.value)}
            className="w-full px-3 py-2.5 text-sm rounded-lg border bg-background focus:ring-2 focus:ring-primary/20 focus:outline-none"
          />
          <div className="text-xs text-muted-foreground mt-1">Leave empty to post immediately</div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-3 pt-2">
          <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
            <input
              type="checkbox"
              checked={isDraft}
              onChange={(e) => setIsDraft(e.target.checked)}
              className="rounded border-muted-foreground/40"
            />
            Save as draft
          </label>
          <div className="flex-1" />
          <Button
            onClick={handleSubmit}
            disabled={!caption.trim() || selectedAccounts.length === 0 || createMutation.isPending}
            className="gap-2"
          >
            {createMutation.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}
            {isDraft ? "Save Draft" : scheduledAt ? "Schedule" : "Post Now"}
          </Button>
        </div>

        {createMutation.isError && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-3 flex items-start gap-2 text-sm text-red-700">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            {String(createMutation.error)}
          </div>
        )}
      </div>
      )
    </PageShell>
  );
}
